import { useState } from "react";
import { ArrowLeft, Check, ChevronUp, MailOpen, PanelLeftClose, PanelLeftOpen, RefreshCw, Trash2 } from "lucide-react";
import { Alert } from "@/components/common/Alert/Alert";
import { Button } from "@/components/shadcn/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/shadcn/empty";
import { Skeleton } from "@/components/shadcn/skeleton";
import { useAvatarColor } from "@/hooks";
import {
  conversationTopic,
  displayName,
  formatListDate,
  formatLongDate,
  initials,
  participantNote,
  plainTextSnippet,
  subjectOrPlaceholder,
} from "../mailFormat";
import { customerEmailsHref, getParticipantAction } from "../participantLinks";
import type { MailConversationState } from "../hooks/useMailConversation";
import type { MailConversation, MailCustomer, MailMessage, MailParticipant } from "../types";
import { MailBodyFrame } from "./MailBodyFrame";
import { MailParticipantLine } from "./MailParticipantLine";
import { MailPill } from "./MailPill";
import { MailTaskLine } from "./MailTaskLine";

type MailReadingPaneProps = {
  state: MailConversationState;
  isListHidden: boolean;
  /** Show which customers the message is filed against, for views that span customers. */
  showCustomers: boolean;
  /** A short confirmation, such as after deleting an email. */
  notice: string | null;
  onToggleList: () => void;
  onBackToList: () => void;
  onRetry: () => void;
  onDelete: (message: MailMessage) => void;
};

const MessageSkeleton = () => (
  <div className="space-y-3 p-5" aria-hidden>
    <Skeleton className="h-6 w-3/4" />
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="h-4 w-2/5" />
    <Skeleton className="mt-6 h-40 w-full" />
  </div>
);

type MessageBodyProps = {
  message: MailMessage;
  /** In a conversation, show just what this reply added, with the quoted history on request. */
  preferUniqueBody: boolean;
};

const MessageBody = ({ message, preferUniqueBody }: MessageBodyProps) => {
  const [showQuoted, setShowQuoted] = useState(false);
  const canTrimQuotes = preferUniqueBody && Boolean(message.uniqueBody?.trim());
  const content = canTrimQuotes && !showQuoted ? message.uniqueBody : message.body;

  if (!content?.trim()) {
    return <p className="p-5 text-sm italic text-gray-500">This email has no content.</p>;
  }

  return (
    <>
      {message.isHtmlBody ? (
        <MailBodyFrame
          key={`${message.id}-${showQuoted ? "full" : "unique"}`}
          html={content}
          title={subjectOrPlaceholder(message.subject)}
        />
      ) : (
        <div className="whitespace-pre-wrap break-words p-5 text-sm leading-relaxed text-gray-800">{content}</div>
      )}
      {canTrimQuotes && (
        <div className="px-3 pb-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-sky-700 hover:bg-sky-50 hover:text-sky-800"
            onClick={() => setShowQuoted(shown => !shown)}
          >
            {showQuoted ? "Show reply only" : "Show full message"}
          </Button>
        </div>
      )}
    </>
  );
};

/** Every customer the conversation is filed against, once each. */
const conversationCustomers = (messages: MailMessage[]): MailCustomer[] =>
  [...new Map(messages.flatMap(message => message.customers).map(customer => [customer.id, customer])).values()];

const ConversationCustomers = ({ customers, mailId }: { customers: MailCustomer[]; mailId: string }) => {
  if (customers.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      {customers.map(customer => (
        <MailPill
          key={customer.id}
          tone="customer"
          href={customerEmailsHref(customer.emailsUrl, mailId)}
          title="Open this email in the customer's Emails tab"
        >
          {customer.name ?? "Customer"}
        </MailPill>
      ))}
    </div>
  );
};

const SENDER_LINK_CLASS =
  "rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** The sender's name, opening their record where it can be opened, then their address. */
const SenderName = ({ sender }: { sender: MailParticipant | null }) => {
  if (!sender) {
    return <span className="text-sm font-semibold text-gray-900">Unknown sender</span>;
  }

  const name = displayName(sender);
  const note = participantNote(sender);
  const action = getParticipantAction(sender);

  const nameElement = action?.kind === "popup" ? (
    <button type="button" onClick={action.open} className={SENDER_LINK_CLASS}>{name}</button>
  ) : action?.kind === "link" ? (
    <a href={action.href} className={SENDER_LINK_CLASS}>{name}</a>
  ) : (
    <span>{name}</span>
  );

  return (
    <div className="min-w-0 text-sm">
      <span className="font-semibold text-gray-900">{nameElement}</span>
      {sender.email && sender.email !== name && (
        <span className="text-gray-500"> &lt;{sender.email}&gt;</span>
      )}
      {note && <div className="text-xs text-gray-500">{note}</div>}
    </div>
  );
};

/**
 * The sender's initials on their avatar colour, the same colour the contact page gives them,
 * so a person is recognisable across the app. An unknown sender stays neutral.
 */
const Avatar = ({ sender, small = false }: { sender: MailParticipant | null; small?: boolean }) => {
  const { getAvatarColor } = useAvatarColor();
  const colourKey = sender?.id ?? sender?.email;
  const size = small ? "size-8 text-xs" : "size-10 text-sm";

  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${size} ${colourKey ? "text-white" : "bg-gray-200 text-gray-600"}`}
      style={colourKey ? { backgroundColor: getAvatarColor(colourKey) } : undefined}
    >
      {initials(sender)}
    </span>
  );
};

type ConversationMessageProps = {
  message: MailMessage;
  isExpanded: boolean;
  /** Null when this is the only email, which cannot be collapsed. */
  onToggle: (() => void) | null;
  preferUniqueBody: boolean;
  onDelete: () => void;
};

/**
 * One email of a conversation. Collapsed, it is a single line that expands on click, like an
 * older reply in Outlook; expanded, it shows the full header and body.
 */
const ConversationMessage = ({ message, isExpanded, onToggle, preferUniqueBody, onDelete }: ConversationMessageProps) => {
  if (!isExpanded && onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={false}
        className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Avatar sender={message.from} small />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-gray-900">
            {message.from ? displayName(message.from) : "Unknown sender"}
          </span>
          <span className="block truncate text-xs text-gray-500">
            {plainTextSnippet(message.uniqueBody ?? message.body, message.isHtmlBody)}
          </span>
        </span>
        <time dateTime={message.dateReceivedUtc} className="shrink-0 text-xs text-gray-500">
          {formatListDate(message.dateReceivedUtc)}
        </time>
      </button>
    );
  }

  return (
    <section aria-label="Message" className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-3">
        <Avatar sender={message.from} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <SenderName sender={message.from} />
            </div>
            <time dateTime={message.dateReceivedUtc} className="shrink-0 pt-0.5 text-xs text-gray-500">
              {formatLongDate(message.dateReceivedUtc)}
            </time>
            <Button
              variant="ghost"
              size="icon"
              className="-mt-1 size-7 text-gray-500 hover:bg-red-50 hover:text-red-700"
              onClick={onDelete}
              aria-label="Delete this email"
              title={message.tasks.length > 0 ? "Delete email (its linked tasks must be deleted first)" : "Delete email"}
            >
              <Trash2 />
            </Button>
            {onToggle && (
              <Button
                variant="ghost"
                size="icon"
                className="-mr-2 -mt-1 size-7"
                onClick={onToggle}
                aria-expanded
                aria-label="Collapse this email"
                title="Collapse"
              >
                <ChevronUp />
              </Button>
            )}
          </div>
          <MailParticipantLine label="To" participants={message.to} emptyText="Unknown" />
          <MailParticipantLine label="Cc" participants={message.cc} />
          <MailTaskLine message={message} />
        </div>
      </div>
      <MessageBody message={message} preferUniqueBody={preferUniqueBody} />
    </section>
  );
};

type ConversationProps = {
  conversation: MailConversation;
  showCustomers: boolean;
  onDelete: (message: MailMessage) => void;
};

/**
 * The selected conversation, newest email first and open, older ones collapsed, the way
 * Outlook shows a conversation. Mount with a key per conversation so each starts collapsed.
 */
const Conversation = ({ conversation, showCustomers, onDelete }: ConversationProps) => {
  const newestFirst = [...conversation.messages].reverse();
  const newest = newestFirst[0];
  const isThread = newestFirst.length > 1;
  const [expandedIds, setExpandedIds] = useState(() => new Set([newest.id]));

  const toggle = (mailId: string) => setExpandedIds(current => {
    const next = new Set(current);
    if (next.has(mailId)) next.delete(mailId);
    else next.add(mailId);
    return next;
  });

  return (
    <article className="min-h-full space-y-3 bg-gray-50 px-3 py-3">
      <header className="rounded-lg border border-l-4 border-gray-200 border-l-sky-600 bg-white px-4 py-3">
        <h2 className="m-0 text-xl font-semibold leading-snug text-gray-900">
          {isThread ? conversationTopic(newest.subject) : subjectOrPlaceholder(newest.subject)}
        </h2>
        {isThread && (
          <p className="m-0 mt-0.5 text-xs text-gray-500">
            {conversation.hasEarlier ? `${newestFirst.length}+` : newestFirst.length} emails in this conversation
          </p>
        )}
        {showCustomers && (
          <ConversationCustomers customers={conversationCustomers(newestFirst)} mailId={newest.id} />
        )}
      </header>

      {newestFirst.map(message => (
        <ConversationMessage
          key={message.id}
          message={message}
          isExpanded={!isThread || expandedIds.has(message.id)}
          onToggle={isThread ? () => toggle(message.id) : null}
          preferUniqueBody={isThread}
          onDelete={() => onDelete(message)}
        />
      ))}

      {conversation.hasEarlier && (
        <p className="m-0 px-1 text-center text-xs text-gray-500">
          Only the most recent {newestFirst.length} emails of this conversation are shown.
        </p>
      )}
    </article>
  );
};

const ConversationContent = ({
  state,
  showCustomers,
  onRetry,
  onDelete,
}: Pick<MailReadingPaneProps, "state" | "showCustomers" | "onRetry" | "onDelete">) => {
  switch (state.status) {
    case "idle":
      return (
        <Empty className="h-full">
          <EmptyHeader>
            <EmptyMedia variant="icon" className="bg-kPurple-50 text-kPurple-500"><MailOpen /></EmptyMedia>
            <EmptyTitle>No email selected</EmptyTitle>
            <EmptyDescription>Choose an email from the list to read it here.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      );
    case "loading":
      return <MessageSkeleton />;
    case "notFound":
      return (
        <div className="p-5">
          <Alert variant="warning" persistent>
            This email isn't available. It may have been removed, or your access to it may be restricted.
          </Alert>
        </div>
      );
    case "error":
      return (
        <div className="space-y-3 p-5">
          <Alert variant="error" persistent>We couldn't load this email.</Alert>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw /> Try again
          </Button>
        </div>
      );
    case "loaded": {
      const { conversation } = state;
      const newestId = conversation.messages[conversation.messages.length - 1]?.id;
      return (
        <Conversation
          key={`${conversation.threadId}-${newestId}`}
          conversation={conversation}
          showCustomers={showCustomers}
          onDelete={onDelete}
        />
      );
    }
  }
};

export const MailReadingPane = ({
  state,
  isListHidden,
  showCustomers,
  notice,
  onToggleList,
  onBackToList,
  onRetry,
  onDelete,
}: MailReadingPaneProps) => (
  <div className="flex h-full min-h-0 flex-col">
    <div className="flex items-center gap-1 border-b border-gray-200 px-2 py-1.5">
      {/* The wrapper owns the breakpoint: Button always carries inline-flex, which would beat md:hidden. */}
      <div className="md:hidden">
        <Button variant="ghost" size="sm" onClick={onBackToList}>
          <ArrowLeft /> Emails
        </Button>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="hidden size-9 md:inline-flex"
        onClick={onToggleList}
        aria-label={isListHidden ? "Show email list" : "Hide email list"}
        title={isListHidden ? "Show email list" : "Hide email list"}
      >
        {isListHidden ? <PanelLeftOpen /> : <PanelLeftClose />}
      </Button>
      <span role="status" className="flex items-center gap-1 px-2 text-xs font-medium text-green-700">
        {notice && <><Check className="size-3.5" aria-hidden /> {notice}</>}
      </span>
    </div>

    <div className="scrollbar-fluent min-h-0 flex-1 overflow-y-auto">
      <ConversationContent state={state} showCustomers={showCustomers} onRetry={onRetry} onDelete={onDelete} />
    </div>
  </div>
);